import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { LeadMagnet } from "@/components/LeadMagnet";
import { LeadSection } from "@/components/LeadSection";
import { StickyCta } from "@/components/StickyCta";
import { itemListSchema } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const TITLE = "Shopify Blog: Praxisbeiträge zu Shop, Technik und Umsatz";
const DESCRIPTION =
  "Beiträge aus echten Shopify-Projekten: Zahlungsarten, Apps, Themes, Migration und Pflichtangaben für deutsche Shops. Ohne Marketing-Floskeln, mit konkreten Zahlen.";

const posts = [
  {
    to: "/blog/shopify-seo-optimierung-guide" as const,
    title: "Shopify SEO Optimierung",
    text: "Kategoriestruktur, Technik, Ladezeit und Inhalte in der Reihenfolge, die tatsächlich Umsatz bringt.",
    date: "2026-09-10",
  },
  {
    to: "/blog/shopify-checkout-optimieren" as const,
    title: "Shopify Checkout optimieren",
    text: "Die häufigsten Abbruchgründe im Kaufabschluss und die Maßnahmen, die am schnellsten wirken.",
    date: "2026-09-10",
  },
  {
    to: "/blog/woocommerce-zu-shopify-migration" as const,
    title: "WooCommerce zu Shopify migrieren",
    text: "Datenübernahme, Weiterleitungsplan und Livegang, ohne die vorhandenen Rankings zu verlieren.",
    date: "2026-09-10",
  },

  {
    to: "/blog/shopify-zahlungsarten-deutschland" as const,
    title: "Shopify Zahlungsarten in Deutschland",
    text: "Welche Zahlarten deutsche Käufer erwarten, was sie kosten und wie sie den Abschluss beeinflussen.",
    date: "2026-09-10",
  },
  {
    to: "/blog/shopify-apps-auswaehlen" as const,
    title: "Shopify Apps auswählen",
    text: "Wann eine App sich lohnt, wann eine Theme-Anpassung günstiger ist, und wie Sie den Bestand aufräumen.",
    date: "2026-09-10",
  },
  {
    to: "/blog/shopify-theme-auswaehlen" as const,
    title: "Shopify Theme auswählen",
    text: "Kostenlos, Premium oder eigenes Theme: die Auswahl an echten Produkten prüfen statt an Demos.",
    date: "2026-09-10",
  },
  {
    to: "/blog/shopify-rechtssicher-deutschland" as const,
    title: "Shopify rechtssicher einrichten",
    text: "Pflichtangaben deutscher Shops und wo sie im Theme hinterlegt werden, ohne Vertrauen zu kosten.",
    date: "2026-09-10",
  },
];

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}/blog` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}/blog` }],
    scripts: [
      itemListSchema(
        posts.map((p) => ({ name: p.title, path: p.to })),
        "Shopify Blog",
      ),
    ],
  }),
  component: BlogIndex,
});

function BlogIndex() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main>
        <section className="veil border-b border-border/60 py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-5">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Blog</p>
            <h1 className="mt-5 max-w-3xl text-4xl font-bold leading-[1.08] md:text-5xl">
              Shopify aus der <span className="text-signal">Projektpraxis</span>
            </h1>
            <p className="mt-5 max-w-xl text-muted-foreground">
              Beiträge zu Fragen, die in echten Projekten auftauchen: Zahlarten, Apps, Themes,
              Migration und Pflichtangaben. Ausführliche Leitfäden finden Sie im{" "}
              <Link to="/ratgeber" className="text-primary underline underline-offset-4">
                Ratgeber
              </Link>
              .
            </p>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground">
              Wählen Sie die Frage, die Sie gerade bremst. Jeder Beitrag zeigt das Problem,
              die sinnvolle Reihenfolge und den nächsten umsetzbaren Schritt.
            </p>
          </div>
        </section>

        <section className="py-14">
          <div className="mx-auto grid max-w-6xl gap-5 px-5 md:grid-cols-2">
            {posts.map((p) => (
              <Link
                key={p.to}
                to={p.to}
                className="shadow-lift group rounded-lg border border-border bg-card p-6 transition-transform hover:-translate-y-0.5"
              >
                <time dateTime={p.date} className="text-xs text-muted-foreground">
                  {new Date(p.date).toLocaleDateString("de-DE", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  })}
                </time>
                <h2 className="mt-2 text-lg font-semibold md:text-xl">{p.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.text}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                  Lesen <ArrowUpRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </section>

        <section className="pb-14">
          <div className="mx-auto max-w-3xl px-5">
            <LeadMagnet source="blog-hub-checkliste" />
          </div>
        </section>

        <LeadSection source="blog-hub" />
      </main>
      <StickyCta />
      <SiteFooter />
    </div>
  );
}
